import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BufferService, LoadingContext } from 'src/M_Components'
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography
} from '@mui/material'
import { CheckCircle, Warning } from '@mui/icons-material'

export const DemurrageCheckClearance = ({ open, setOpen, demurrage, onSuccess }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)

    // Steps: 'confirm' → user sees prompt → 'result' → user sees outcome
    const [step, setStep] = useState('confirm')
    const [item, setItem] = useState(null)       // DemurrageClearanceItem for the selected demurrage
    const [errorMessage, setErrorMessage] = useState(null)

    const extractErrorMessage = (err) => {
        const data = err?.response?.data ?? err?.response ?? err
        if (typeof data === 'string') return data
        return data?.message ?? data?.detail ?? JSON.stringify(data)
    }

    const handleConfirm = async () => {
        if (!demurrage?.DemurrageControlId) {
            ctx.toastMessages('error', '', t('This demurrage does not have an associated control'))
            return
        }

        ctx.setIsLoad(true)

        const checkService = new BufferService(
            `DemurrageControl/${demurrage.DemurrageControlId}/CheckClearance/${demurrage.Id}`,
            'secondary'
        )
        const response = await checkService.post(null)
        ctx.setIsLoad(false)

        // BufferService.post catches all axios errors and returns them as
        // resolved values — check for AxiosError on the resolved response.
        if (response?.name === 'AxiosError') {
            setErrorMessage(extractErrorMessage(response))
            setStep('result')
            return
        }

        // Find the item matching the selected demurrage in the returned Items array
        const matched = (response?.Items ?? []).find(i => i.DemurrageId === demurrage.Id)
        setItem(matched ?? null)
        setStep('result')

        if (matched?.CanClear && onSuccess) onSuccess()
    }

    const handleClose = () => {
        setOpen(false)
        setStep('confirm')
        setItem(null)
        setErrorMessage(null)
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: ctx.primaryColor, fontWeight: 'bold' }}>
                {t('Check Clearance')} — {t('Demurrage')} {demurrage?.Id}
            </DialogTitle>

            <DialogContent dividers>

                {/* Step 1 — confirmation prompt */}
                {step === 'confirm' && (
                    <Typography>
                        {t('This will check if demurrage')} <strong>{demurrage?.Id}</strong> {t('can be cleared automatically. Do you want to proceed?')}
                    </Typography>
                )}

                {/* Step 2a — can be cleared */}
                {step === 'result' && !errorMessage && item?.CanClear && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'green' }}>
                        <CheckCircle />
                        <div>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                {t('Demurrage can be cleared — no billing required.')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#2e7d32', marginTop: '4px' }}>
                                {t('Chargeable days')}: {item.ChargeableDays} / {t('Free days')}: {item.FreeDays}
                            </Typography>
                        </div>
                    </div>
                )}

                {/* Step 2b — cannot be cleared */}
                {step === 'result' && !errorMessage && item && !item.CanClear && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <Warning style={{ color: 'orange', marginTop: '2px', flexShrink: 0 }} />
                        <div>
                            <Typography sx={{ fontWeight: 'bold', marginBottom: '4px', color: 'orange' }}>
                                {t('Cannot be cleared — billing is required')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#795548' }}>
                                {t('Chargeable days')}: {item.ChargeableDays} / {t('Free days')}: {item.FreeDays}
                            </Typography>
                            {item.Reason && (
                                <Typography variant="body2" sx={{ color: '#795548', marginTop: '4px' }}>
                                    {item.Reason}
                                </Typography>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 2c — no matching item (fallback) */}
                {step === 'result' && !errorMessage && !item && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <Warning style={{ color: 'orange', marginTop: '2px', flexShrink: 0 }} />
                        <div>
                            <Typography>
                                {t('No clearance result found for this demurrage.')}
                            </Typography>
                        </div>
                    </div>
                )}

                {/* Step 2d — API error */}
                {step === 'result' && errorMessage && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <Warning style={{ color: 'red', marginTop: '2px', flexShrink: 0 }} />
                        <div>
                            <Typography sx={{ fontWeight: 'bold', marginBottom: '4px', color: 'red' }}>
                                {t('Cannot be cleared')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#c0392b' }}>
                                {errorMessage}
                            </Typography>
                        </div>
                    </div>
                )}

            </DialogContent>

            <DialogActions>
                {step === 'confirm' && (
                    <>
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            sx={{ backgroundColor: 'var(--color-green)', color: 'var(--color-white)' }}
                        >
                            {t('Confirm')}
                        </Button>
                        <Button
                            onClick={handleClose}
                            variant="contained"
                            sx={{ backgroundColor: 'var(--color-red)', color: 'var(--color-white)' }}
                        >
                            {t('Cancel')}
                        </Button>
                    </>
                )}
                {step === 'result' && (
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        sx={{ backgroundColor: ctx.primaryColor, color: 'var(--color-white)' }}
                    >
                        {t('Close')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}